import React, { useMemo } from 'react';
import { Dropdown, DropdownButton, Spinner } from 'react-bootstrap';
import { DateRangePicker } from 'react-date-range';
import moment from 'moment';

import Loader from 'reusable/Loader';
import {
  useFetchCreativeIdListQuery,
  useFetchMetadataQuery,
} from 'services/api/creativePreview';
import CreativeBlock from './components/CreativeBlock';
import { CreativePreviewStyles } from './style';
import Pagination from './components/Pagination';
import { get } from 'lodash';
import useQuery from 'reusable/customHooks/useQuery';
import Filters from './components/Filters';
import { useLocation } from 'react-router-dom';
import { MdRefresh } from 'react-icons/md';
import useHeader from 'containers/App/Header/useHeader';

function CreativePreview() {
  useHeader('Creatives Preview');
  const [query, setQuery] = useQuery();
  const location = useLocation();

  const startDate = query.get('startDate');
  const endDate = query.get('endDate');
  const pageIndex = Number(query.get('page')) || 1;
  const pageSize = Number(query.get('pageSize')) || 5;
  const status = query.get('status');

  const {
    data = [],
    isLoading,
    isFetching,
    fulfilledTimeStamp,
    refetch,
  } = useFetchCreativeIdListQuery({
    startDate,
    endDate,
    includePending: status ? status.includes('Pending') : false,
  });

  const { data: filters = [] } = useFetchMetadataQuery('filters');

  const getValue = (label, isMulti = false) => {
    const res = query.get(label);
    if (!res) return undefined;
    return isMulti ? res.split(',') : res;
  };

  const filteredData = useMemo(
    () =>
      data.filter(el => {
        const isFiltered = filters.every(filter => {
          const filterValue = getValue(filter.key, filter.isMulti);
          if (!filterValue || !filterValue.length) return true;
          if (filter.isMulti)
            return filterValue.includes(String(get(el, filter.path)));
          return filterValue === String(get(el, filter.path));
        });
        return isFiltered;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, location.search],
  );

  const startDateUTC = new Date(startDate || new Date());
  const endDateUTC = new Date(endDate || new Date());

  const ranges = {
    startDate: new Date(
      startDateUTC.getTime() + startDateUTC.getTimezoneOffset() * 60 * 1000,
    ),
    endDate: new Date(
      endDateUTC.getTime() + endDateUTC.getTimezoneOffset() * 60 * 1000,
    ),
    key: 'selection',
  };

  return (
    <CreativePreviewStyles>
      <div className="preview-date-container">
        <div className="date-title">Preview Dates (UTC) : </div>
        <DropdownButton
          title={`${ranges.startDate.toDateString()} - ${ranges.endDate.toDateString()}`}
          autoClose="outside"
        >
          <Dropdown.Item as={'div'} onClick={e => e.stopPropagation()}>
            <DateRangePicker
              ranges={[ranges]}
              onChange={range => {
                setQuery(
                  'startDate',
                  range.selection.startDate.toLocaleString('sv').split(' ')[0],
                );
                setQuery(
                  'endDate',
                  range.selection.endDate.toLocaleString('sv').split(' ')[0],
                );
              }}
            />
          </Dropdown.Item>
        </DropdownButton>
        <div className="refetch-container">
          {isFetching || isLoading ? (
            <Spinner animation="border" />
          ) : (
            <MdRefresh className="refresh-icon" onClick={() => refetch()} />
          )}
          <div>Last Fetched : {moment(fulfilledTimeStamp).format('llll')}</div>
        </div>
        {isLoading ? null : <Pagination dataLength={filteredData.length} />}
      </div>
      <Filters data={data} />
      <div className="preview-container">
        {isLoading || isFetching ? <Loader /> : null}
        {filteredData
          .slice((pageIndex - 1) * pageSize, pageIndex * pageSize)
          .map(creative => (
            <CreativeBlock
              key={get(creative, '_id')}
              data={creative}
              creativeFetchTime={fulfilledTimeStamp}
            />
          ))}
      </div>
    </CreativePreviewStyles>
  );
}

export default CreativePreview;
